/**
 * Contas a Pagar / Contas a Receber v2 (mockup Fase 5 V2.2).
 *
 * Vista operacional ("quem devo? quem me deve?"): .fin2, KPI strip (Em aberto/
 * Pagos|Recebidos/Vencidos/Total), tabchips (Em aberto/Pagos|Recebidos/
 * Vencidos/Todos), dtable (Vencimento/Histórico/Categoria/Conta/Obra/Valor/
 * Status) + ação "Marcar pago/recebido" via slide-over quick-pay. "Nova
 * despesa/receita" reusa o slide-over de Novo lançamento (tipo pré-fixado).
 *
 * Reusa /api/lancamentos + /confirmar — sem mudança de lógica/backend.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckCircle, FiPlus } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  confirmarLancamento, formatarReais, listarLancamentos,
} from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";

import { NovoLancamentoPanel } from "../Lancamentos/LancamentoPanels";

function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}
function dataCurta(iso) {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return d && m ? `${d}/${m}` : iso;
}

// Regime híbrido (ADR-0028): a CAIXA define se já foi pago/recebido.
//
// Três baldes MUTUAMENTE EXCLUSIVOS — "vencidos" não está dentro de
// "aberto" aqui. Quem quiser "tudo que ainda não foi pago" usa
// `estaEmAberto`, e não a comparação direta com "aberto": foi essa
// confusão que o reteste de 2026-08-18 pegou.
function classificar(l, hoje) {
  if (l.data_caixa) return "pagos";
  return (l.data_competencia || "") < hoje ? "vencidos" : "aberto";
}

/**
 * "Em aberto" = tudo que ainda não foi pago, vencido ou não.
 *
 * Reteste 2026-08-18: o KPI dizia "EM ABERTO R$ 44.500 / 3 documentos" e
 * a lista logo abaixo mostrava 1 documento de R$ 32.500. O KPI somava
 * aberto + vencidos (o `else` pegava os dois); a aba filtrava só o balde
 * "aberto". Duas réguas para a mesma palavra na mesma tela — e o gestor
 * sem saber em qual acreditar.
 *
 * A régua certa é a contábil: em aberto é o que se deve, e vencer não
 * deixa de dever. A aba "Vencidos" continua existindo como recorte.
 */
function estaEmAberto(l, hoje) {
  return classificar(l, hoje) !== "pagos";
}


function QuickPayPanel({ open, onClose, lanc, isReceber, onConfirmed }) {
  const { request } = useAuth();
  const [data, setData] = useState(hojeISO());
  useEffect(() => { if (open) setData(hojeISO()); }, [open]);
  if (!lanc) return null;
  const confirmar = async () => {
    await confirmarLancamento(request, lanc.id, { data_caixa: data });
    await onConfirmed?.();
    onClose();
  };
  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={isReceber ? "Marcar como recebido" : "Marcar como pago"}
      subtitle="Confirme a data de caixa"
      width="min(95vw, 380px)"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <AsyncButton onClick={confirmar}
                       successMessage={isReceber ? "Recebimento registrado." : "Pagamento registrado."}
                       silentError>Confirmar baixa</AsyncButton>
        </>
      }
    >
      <div className="fin2-panel">
        <div className="quick-pay">
          <div>
            <div className="tiny mono muted" style={{ marginBottom: 4 }}>{lanc.descricao}</div>
            <div className="qp-amount">
              <span style={{ fontSize: 16, color: "var(--ink-3)" }}>R$</span> {formatarReais(lanc.valor).replace("R$", "").trim()}
            </div>
          </div>
          <div className="input-group">
            <label className="input-label">Data de caixa (baixa)</label>
            <input className="input" type="date" value={data} onChange={(e) => setData(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Conta</label>
            <input className="input" value={lanc.conta_nome || "—"} readOnly disabled />
          </div>
          <div style={{ padding: "11px 13px", background: "var(--info-bg)", borderRadius: "var(--radius-md)", fontSize: 12, color: "var(--info-text)", lineHeight: 1.5 }}>
            A baixa registra a data de caixa e move o lançamento para o Fluxo realizado.
          </div>
        </div>
      </div>
    </SlideOverPanel>
  );
}


export default function ContasView({ tipo }) {
  const isReceber = tipo === "ENTRADA";
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("aberto");
  const [novoAberto, setNovoAberto] = useState(false);
  const [quickPay, setQuickPay] = useState(null); // lançamento sendo baixado

  const podeLancar = isMaster || hasAllPermissions(["financeiro:lancar"]);
  const hoje = useMemo(() => hojeISO(), []);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const payload = await listarLancamentos(request, { tipo });
      setItems(Array.isArray(payload) ? payload : payload?.results || []);
    } catch (err) {
      setError(err?.message || "Erro ao carregar.");
      setItems([]);
    } finally { setLoading(false); }
  }, [request, tipo]);

  useEffect(() => { carregar(); }, [carregar]);

  const kpis = useMemo(() => {
    let aberto = 0, pagos = 0, vencidos = 0, total = 0, nAberto = 0;
    for (const l of items) {
      const v = Number(l.valor || 0); total += v;
      const s = classificar(l, hoje);
      if (s === "pagos") pagos += v;
      if (estaEmAberto(l, hoje)) { aberto += v; nAberto += 1; }
      if (s === "vencidos") vencidos += v;
    }
    return { aberto, pagos, vencidos, total, nAberto };
  }, [items, hoje]);

  const filtrados = useMemo(() => {
    const list = items.filter((l) => {
      if (tab === "todos") return true;
      // A aba "Em aberto" tem que casar com o KPI de mesmo nome.
      if (tab === "aberto") return estaEmAberto(l, hoje);
      return classificar(l, hoje) === tab;
    });
    return list.sort((a, b) => (a.data_competencia || "").localeCompare(b.data_competencia || ""));
  }, [items, tab, hoje]);

  const TABS = [
    { id: "aberto", label: "Em aberto" },
    { id: "pagos", label: isReceber ? "Recebidos" : "Pagos" },
    { id: "vencidos", label: "Vencidos" },
    { id: "todos", label: "Todos" },
  ];

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">{isReceber ? "Contas a Receber" : "Contas a Pagar"}</span>
          </div>
          <h1 className="page-title">{isReceber ? "Contas a Receber" : "Contas a Pagar"}</h1>
          <div className="page-sub">Vencimentos · ordenado por data</div>
        </div>
        {podeLancar && (
          <button type="button" className="btn btn--primary" onClick={() => setNovoAberto(true)}>
            <FiPlus size={14} aria-hidden="true" /> {isReceber ? "Nova receita" : "Nova despesa"}
          </button>
        )}
      </div>

      <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
        <div className="kpi-v2"><div className="lbl">Em aberto</div><div className="val">{formatarReais(kpis.aberto)}</div><div className="meta">{kpis.nAberto} {isReceber ? "documento(s)" : "conta(s)"}</div></div>
        <div className="kpi-v2"><div className="lbl">{isReceber ? "Recebidos" : "Pagos"}</div><div className="val">{formatarReais(kpis.pagos)}</div><div className="meta">realizados</div></div>
        <div className="kpi-v2"><div className="lbl">Vencidos</div><div className="val" style={{ color: kpis.vencidos > 0 ? "var(--danger-text)" : undefined }}>{formatarReais(kpis.vencidos)}</div><div className="meta">{kpis.vencidos > 0 ? "em atraso" : "nada em atraso"}</div></div>
        <div className="kpi-v2"><div className="lbl">Total do período</div><div className="val">{formatarReais(kpis.total)}</div><div className="meta">{items.length} lançamento(s)</div></div>
      </div>

      {error && <div className="empty"><div className="msg">{error}</div></div>}

      <div className="table-wrap">
        <div className="flex between" style={{ padding: "12px 18px", borderBottom: "1px solid var(--border)", gap: 12, flexWrap: "wrap" }}>
          <div className="tabchip-group">
            {TABS.map((t) => (
              <div key={t.id} className={"tabchip" + (tab === t.id ? " active" : "")}
                   role="button" tabIndex={0} onClick={() => setTab(t.id)}
                   onKeyDown={(e) => e.key === "Enter" && setTab(t.id)}>{t.label}</div>
            ))}
          </div>
          <span className="tiny mono muted">{filtrados.length} {tab === "aberto" ? "EM ABERTO" : "RESULTADOS"}</span>
        </div>

        <table className="dtable">
          <colgroup>
            <col style={{ width: 90 }} /><col /><col style={{ width: 130 }} />
            <col style={{ width: 130 }} /><col style={{ width: 120 }} />
            <col style={{ width: 120 }} /><col style={{ width: 110 }} /><col style={{ width: 150 }} />
          </colgroup>
          <thead>
            <tr>
              <th>Vencimento</th><th>Histórico</th><th>Categoria</th><th>Conta</th>
              <th>Obra / pedido</th><th className="num" style={{ textAlign: "right" }}>Valor</th>
              <th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ padding: 24, textAlign: "center" }} className="muted">Carregando…</td></tr>}
            {!loading && filtrados.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 0 }}>
                <div className="empty"><div className="msg">
                  {tab === "aberto" ? `Nenhuma ${isReceber ? "conta a receber" : "conta a pagar"} em aberto.`
                    : tab === "pagos" ? (isReceber ? "Nenhum recebimento registrado." : "Nenhum pagamento registrado.")
                    : tab === "vencidos" ? "Nada vencido." : "Nenhum lançamento."}
                </div></div>
              </td></tr>
            )}
            {filtrados.map((l) => {
              const s = classificar(l, hoje);
              const vencido = s === "vencidos";
              return (
                <tr key={l.id} style={vencido ? { background: "var(--danger-bg)" } : undefined}>
                  <td className="mono" style={vencido ? { color: "var(--danger-text)", fontWeight: 600 } : undefined}>{dataCurta(l.data_competencia)}</td>
                  <td className="ell" title={l.descricao}>{l.descricao}</td>
                  <td><span className="badge neutral no-dot" style={{ fontSize: 11 }}>{l.categoria_nome || "—"}</span></td>
                  <td className="ell">{l.conta_nome || "—"}</td>
                  <td>{l.pedido_numero ? <span className="mono">{l.pedido_numero}</span> : <span className="muted">—</span>}</td>
                  <td className="num" style={{ textAlign: "right", fontWeight: 600, color: isReceber ? "var(--success-text)" : "var(--ink)" }}>{formatarReais(l.valor)}</td>
                  <td>
                    {s === "pagos" ? <span className="badge success no-dot">{isReceber ? "Recebido" : "Pago"}</span>
                      : s === "vencidos" ? <span className="badge danger">Vencido</span>
                      : <span className="badge warning">{isReceber ? "A receber" : "A vencer"}</span>}
                  </td>
                  <td>
                    {s !== "pagos" && podeLancar && (
                      <button type="button" className="btn btn--secondary btn--sm" onClick={() => setQuickPay(l)}>
                        <FiCheckCircle size={12} aria-hidden="true" /> {isReceber ? "Marcar recebido" : "Marcar pago"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex between" style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", fontSize: 12 }}>
          <span className="muted">
            Em aberto soma <strong style={{ color: "var(--ink)" }}>{formatarReais(kpis.aberto)}</strong>
            {kpis.vencidos > 0 ? ` · ${formatarReais(kpis.vencidos)} vencido` : " · sem contas vencidas"}
          </span>
        </div>
      </div>

      <NovoLancamentoPanel open={novoAberto} onClose={() => setNovoAberto(false)}
                           onSaved={carregar} tipoInicial={tipo} />
      <QuickPayPanel open={!!quickPay} lanc={quickPay} isReceber={isReceber}
                     onClose={() => setQuickPay(null)} onConfirmed={carregar} />
    </PageContainer>
  );
}
